import { useState, useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import { SingleItemAutocomplete } from '~components/autocomplete/single-item-autocomplete'
import { useGetPortInfoAutocomplete } from '~hooks/queries/port-info/use-get-port-info-autocomplete'
import { inputWidth } from '../../add-visit-flow'

type FieldType = 'terminal' | 'berth'

interface PortInfoFieldAutocompleteProps {
  fieldType: FieldType
  value?: string
  onChange: (value: string | undefined) => void
  error?: boolean
  disabled?: boolean
}

const fieldConfig: Record<
  FieldType,
  { label: string; apiField: 'terminalName' | 'berthName' }
> = {
  terminal: { label: 'Terminal *', apiField: 'terminalName' },
  berth: { label: 'Berth *', apiField: 'berthName' },
}

export function PortInfoFieldAutocomplete({
  fieldType,
  value,
  onChange,
  error = false,
  disabled = false,
}: PortInfoFieldAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || '')
  const { label, apiField } = fieldConfig[fieldType]

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const shouldFetch = inputValue.length >= 3

  const { data, isLoading } = useGetPortInfoAutocomplete({
    [apiField]: shouldFetch ? inputValue : undefined,
    enabled: shouldFetch,
  })

  const options = shouldFetch && data ? data : []

  return (
    <SingleItemAutocomplete<string>
      sx={{ width: inputWidth }}
      label={label}
      input={value || ''}
      setInputValue={setInputValue}
      options={options}
      freeSolo
      placeholder="Type 3 characters to search"
      hasError={error}
      disabled={disabled}
      getOptionLabel={(option: string) => option}
      isOptionEqualToValue={(option: string, val: string) => option === val}
      onChangeHandler={(_e, newValue) => {
        if (typeof newValue === 'string') {
          onChange(newValue || undefined)
        } else {
          onChange(undefined)
        }
      }}
      onBlur={() => {
        if (inputValue && inputValue !== value) {
          onChange(inputValue)
        }
      }}
      filterOptions={(x: string[]) => x}
      endAdornment={isLoading ? <CircularProgress size={20} /> : null}
    />
  )
}
